import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import "./SignIn.css";
import logo from "assets/img/samu01_logo.png";
import logoomnidoc from "assets/img/logoomnidoc.png";
import { login, verifyOtp, checkToken, getUser, logout } from "../../../api/api";
import { usePermissions } from "hooks/usePermissions";


function SignIn() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshPermissions } = usePermissions();


  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [step, setStep] = useState("login"); // "login" or "otp"
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  const togglePassword = () => setShow(!show);

  // Fonction helper pour rediriger selon le rôle
  const redirectByRole = useCallback(async () => {
    try {
      // Refresh permissions before redirecting
      await refreshPermissions();

      const userData = await getUser();

      const userRole = userData.rôle || userData['rÃ´le'] || userData.role || '';
      const normalizedRole = userRole.toLowerCase();

      if (normalizedRole === 'médecin' || normalizedRole === 'medecin') {
        navigate('/medecin/default');
      } else {
        navigate('/admin/default');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du rôle:', error);
      navigate('/admin/default');
    }
  }, [navigate, refreshPermissions]);

  useEffect(() => {
    const checkTokenEffect = async () => {
      const isAccessDenied = Boolean(location.state?.accessDenied);
      if (isAccessDenied) {
        const toastKey = location.state?.accessDeniedKey || "access-denied";

        try {
          await logout();
        } catch (error) {
        }

        if (!sessionStorage.getItem(toastKey) && location.state?.showAccessDeniedToast !== false) {
          toast({
            title: "Accès refusé",
            description: "Vous n'avez pas les permissions nécessaires pour accéder à cette page",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top"
          });
          sessionStorage.setItem(toastKey, "1");
        }

        navigate("/auth/sign-in", { replace: true, state: {} });
        return;
      }

      try {
        const response = await checkToken();
        if (response.data.valid) {
          // Si le token est valide, on redirige selon le rôle
          await redirectByRole();
        }
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error("Erreur checkToken:", error);
        }
      }
    };
    checkTokenEffect();
  }, [navigate, location.state, redirectByRole, toast]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(email, password);

      if (response.data.userId) {
        setUserId(response.data.userId);
        setStep("otp");
        toast({
          title: "OTP envoyé",
          description: "Un code de vérification a été envoyé par mail.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      } else if (response.data.user) {
        await redirectByRole();
      } else {
        toast({
          title: "Erreur de connexion",
          description: "Réponse inattendue du serveur.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur de connexion',
        description: error.response?.data?.message || 'Email ou mot de passe incorrect',
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await verifyOtp(userId, otp);

      if (response.data.user) {
        await redirectByRole();
      } else {
        toast({
          title: "OTP invalide",
          description: "Le code saisi est incorrect.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Le code OTP est incorrect ou a expiré.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  return (
    <div className="auth-wrapper">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-title">Authentification</div>
        <div className="auth-header-actions">
          <div className="auth-language-selector">
            🇫🇷 Français ▼
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="auth-main-container">
        <div className="auth-card">
          {/* Logo Section */}
          <div className="auth-logo-container">
            <img src={logo} alt="OMNIDOC Logo" className="auth-logo" />
          </div>

          {/* Form Title & Subtitle */}
          <h1 className="auth-form-title">
            {step === "login" ? "Connectez-vous pour continuer" : "Validez votre connexion"}
          </h1>

          {/* Form */}
          {step === "login" ? (
            <form id="loginForm" onSubmit={handleLogin}>
              <div className="auth-form-group">
                <label htmlFor="email" className="auth-form-label">E-mail</label>
                <input
                  type="email"
                  id="email"
                  className="auth-form-input"
                  placeholder="mail@omnidoc.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="password" title="Mot de passe" className="auth-form-label">Mot de passe</label>
                <div className="auth-password-wrapper">
                  <input
                    type={show ? "text" : "password"}
                    id="password"
                    className="auth-form-input"
                    placeholder="••••••••••••••••••••••"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type="button" className="auth-password-toggle" onClick={togglePassword}>
                    {show ? "🙊" : "👁"}
                  </button>
                </div>
              </div>

              <div className="auth-checkbox-group">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <label htmlFor="remember">Souviens-toi de moi</label>
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? "Connexion..." : "Se connecter"}
              </button>

              <div className="auth-forgot-password">
                <NavLink to="/auth/forgot-password">Mot de passe oublié ?</NavLink>
              </div>
            </form>
          ) : (
            <form id="otpForm" onSubmit={handleVerifyOtp}>
              <div className="auth-form-group">
                <label htmlFor="otp" className="auth-form-label">Code OTP</label>
                <input
                  type="text"
                  id="otp"
                  className="auth-form-input"
                  placeholder="Code reçu par mail"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoFocus
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? "Vérification..." : "Vérifier le code"}
              </button>

              <div className="auth-forgot-password">
                <button
                  type="button"
                  onClick={() => setStep("login")}
                  style={{ background: "none", border: "none", color: "#0066cc", cursor: "pointer", fontSize: "0.9rem" }}
                >
                  Retour à la connexion
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <div className="auth-card-footer">

      </div>

      <div className="auth-page-footer">
        <p>En continuant, vous acceptez nos <NavLink to="#">Conditions d'utilisation</NavLink> et notre <NavLink to="#">Politique de confidentialité</NavLink>. <a href="mailto:support@omnidoc.com">Contactez le support</a></p>
      </div>
    </div>
  );
}

export default SignIn;


